import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  CollapsableProposal,
  EditQuoteDialog,
  FormState,
  GeneratePresentationDrawer,
  getOtherReasonParts,
  Header,
  MARKETING_PRESENTATION_REASON_LABELS,
  OTHER_REASON,
  ProductType,
  Quote,
  REQUOTE_REASONS,
  RFP,
  Status,
  Submission,
  SubmissionNotification,
  TrackerCard,
  useFormState,
  StatusType,
} from "@platform/carrier-tracker-components";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useApi } from "@platform/api";

import { CircularProgressCentered } from "../../../../components/CircularProgressCentered";
import { useAnalyticsData } from "../../../../contexts/analyticsContext";
import { useAnalyticsEvents } from "../../../../hooks/useAnalyticsEvents";
import useMarketingPresentation from "../../../../hooks/useMarketingPresentation";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import { sortProductsByQuoteProductType } from "../../../../pages/RFP/utils";
import envVariables from "../../../../projectEnvVariables";
import { formatDate, extractErrorMessage } from "../../../../utils";
import { CarrierTrackerContext } from "../../types";

import ErrorMessage from "./error-message";
import ErrorProposalDialog, { Proposal } from "./error-proposal-dialog";
import QuoteDetails from "./quote-details";

function CarrierTracker(): React.JSX.Element {
  const { id: rfpId } = useParams();
  const { ecoRfpId, clientName, brokerExternalId } =
    useOutletContext<CarrierTrackerContext>();
  const {
    trackCarrierTrackerMarketingPresentation,
    trackCarrierTrackerQuoteSubmitted,
    trackCarrierTrackerQuoteModal,
    trackCarrierTrackerRequoteNeeded,
    trackCarrierIncludedInMarketingPresentation,
  } = useAnalyticsEvents();
  const { dispatch: analyticsDispatch } = useAnalyticsData();
  const axiosInstance = useApi();
  const [openPresentationDialog, setOpenPresentationDialog] =
    useState<boolean>(false);
  const [showOpenPresentationCTA, setShowOpenPresentationCTA] =
    useState<boolean>(false);
  const [presentationData, setPresentationData] =
    useState<PresentationDataResponse | null>(null);
  const [folderPath, setFolderPath] = useState<string>("");
  const [generatePresentationLoading, setGeneratePresentationLoading] =
    useState<boolean>(false);
  const [submitPresentationLoading, setSubmitPresentationLoading] =
    useState<boolean>(false);
  const [presentationTemplates, setPresentationTemplates] = useState<
    PresentationTemplate[]
  >([]);
  const { createToastNotification } = useToastNotificationService();
  const { data: marketingPresentationData, isLoading: isMarketingLoading } =
    useMarketingPresentation(rfpId, brokerExternalId);

  useEffect(() => {
    if (marketingPresentationData?.marketingPresentation) {
      setShowOpenPresentationCTA(true);
      setPresentationData(marketingPresentationData);
    }
  }, [marketingPresentationData]);

  const useGeneratePresentation = (
    rfpId: number,
    onSuccess?: () => void,
    onError?: () => void,
  ): ReturnType<
    typeof useMutation<
      GeneratePresentationResponse,
      Error,
      Omit<GeneratePresentationPayload, "rfpId">
    >
  > => {
    const axiosInstance = useApi();
    const { createToastNotification } = useToastNotificationService();

    return useMutation<
      GeneratePresentationResponse,
      Error,
      Omit<GeneratePresentationPayload, "rfpId">
    >({
      mutationFn: async ({
        marketingPresentation,
        templateFolderPath,
        orderedCarriers,
      }: Omit<GeneratePresentationPayload, "rfpId">) => {
        const responseData = await axiosInstance.post(
          `/brokerportal/v1/rfp/${rfpId}/marketing-presentation`,
          {
            marketingPresentation,
            templateFolderPath,
            orderedCarriers,
          },
        );
        return responseData.data as GeneratePresentationResponse;
      },
      onSuccess: (response) => {
        window.open(`${response.aquarium.fullPath}?web=1`);
        onSuccess?.();
      },
      onError: (err: unknown) => {
        createToastNotification({
          severity: "error",
          message: extractErrorMessage(err),
        });
        onError?.();
      },
    });
  };

  const query = useQuery<RFP, Error>({
    queryKey: ["aquarium", "rfp", ecoRfpId],
    queryFn: () =>
      axiosInstance
        .get(
          `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/aquarium/v1/rfps/${ecoRfpId}`,
        )
        .then((res) => res.data as RFP),
  });

  const [selectedQuote, setSelectedQuote] = useState<ExtendedQuote | null>(
    null,
  );
  const [formState, setFormState, dispatchFormState] = useFormState();
  const showMarketingPresentationCTA = useFeatureIsOn(
    "marketing-template-selection",
  );
  function refetchRfp(): void {
    query.refetch();
  }

  const { mutate: generatePresentation } = useGeneratePresentation(
    Number(rfpId),
    () => {
      trackCarrierTrackerMarketingPresentation({
        status: "generated",
      });
      setOpenPresentationDialog(false);
      setSubmitPresentationLoading(false);
      setGeneratePresentationLoading(false);
    },
    () => {
      trackCarrierTrackerMarketingPresentation({
        status: "failed",
      });
      setGeneratePresentationLoading(false);
      setSubmitPresentationLoading(false);
    },
  );

  const quoteMutation = useMutation<Quote, Error, QuoteMutation>({
    mutationFn: ({ quoteId, ...body }) =>
      axiosInstance.put(
        `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/aquarium/v1/quotes/${quoteId}`,
        body,
      ),
    onSuccess: () => {
      trackCarrierTrackerQuoteSubmitted({
        status: "success",
      });
      createToastNotification({
        message: "Quote has been successfully submitted",
        severity: "success",
      });
      handleCleanState();
      refetchRfp();
    },
    onError: (error) => {
      trackCarrierTrackerQuoteSubmitted({
        status: "failed",
      });
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });

  if (query.isLoading) {
    return <CircularProgressCentered />;
  }
  if (query.isError) {
    return (
      <ErrorMessage>
        {extractErrorMessage(query.error, NO_INFORMATION_AVAILABLE)}
      </ErrorMessage>
    );
  }

  if (!query.data) {
    return <ErrorMessage>{NO_INFORMATION_AVAILABLE}</ErrorMessage>;
  }
  const handleClosePresentationDialog = (): void => {
    setOpenPresentationDialog(false);
  };

  function handleCloseQuoteDialog(): void {
    if (quoteMutation.isPending) {
      return;
    }
    trackCarrierTrackerQuoteModal({
      status: "closed",
    });
    handleCleanState();
  }

  function handleCleanState(): void {
    setSelectedQuote(null);
    analyticsDispatch({
      quoteProductType: "",
      quoteCarrierName: "",
    });
    dispatchFormState({ type: "RESET" });
  }

  const { carriers = [], ...rfpData } = query.data;

  if (carriers.length === 0) {
    return (
      <Box display="flex" flexDirection="column" gap={3} height="100%">
        <Header showCTA={false} />
        <Box
          display="flex"
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
        >
          <Typography variant="header-large">No carriers found</Typography>
        </Box>
      </Box>
    );
  }

  const allQuotes = carriers
    .flatMap((carrier) => carrier.submissions)
    .flatMap((submission) => submission.quotes || []);

  const showCTA = allQuotes.length > 0;
  const isIncludeInMarketingPresentation = allQuotes.some(
    (quote) => quote.includeInMarketingPresentation,
  );

  const handleQuoteSubmit = async (): Promise<void> => {
    const requoteReason = formState.requoteNeeded
      ? formState.requoteReason === OTHER_REASON
        ? formState.requoteOtherReason
        : formState.requoteReason
      : undefined;
    const marketingTemplateExcludeReason =
      formState.marketingTemplateExcludeReason === OTHER_REASON
        ? formState.marketingTemplateExcludeOtherReason
        : formState.marketingTemplateExcludeReason || null;

    const payload = {
      requoteReason,
      ecoRFPId: ecoRfpId,
      quoteId: selectedQuote.id,
      isApproved: formState.isApprovedQuote,
      isCompetitive: formState.isCompetitive,
      includeInMarketingPresentation: formState.includeInMarketingPresentation,
      packageRatesApply: formState.isPackageRatesApply,
      marketingTemplateExcludeReason,
      requoteRequired: formState.requoteNeeded,
      ecoProposalId: selectedQuote.ecoProposalId,
      ecoRFPVersion: selectedQuote.ecoRFPVersion,
      productType: selectedQuote.productType,
      productPlanType: selectedQuote.productPlanType,
      shareIdentifier: selectedQuote.shareIdentifier,
    };
    trackCarrierTrackerRequoteNeeded({
      status: formState.requoteNeeded ? "needed" : "not_needed",
      requoteReason,
    });
    trackCarrierIncludedInMarketingPresentation({
      coverage: selectedQuote.displayName,
      requoteReason,
      status: formState.includeInMarketingPresentation
        ? "included"
        : "excluded",
    });
    quoteMutation.mutate(payload);
  };

  const handleOnSelectedRow = (
    quote: ExtendedQuote,
    carrierName: string,
    productType: string,
  ): void => {
    if (!quote.ecoProposalId) {
      createToastNotification({
        message:
          "This quote cannot be selected because it has no associated proposal.",
        severity: "warning",
      });
      return;
    }
    trackCarrierTrackerQuoteModal({
      carrierName,
      coverage: productType,
      requoteReason: quote.requoteReason || "-",
      status: "open",
    });

    setSelectedQuote(quote);
    const { reason: requoteReason, otherReason: requoteOtherReason } =
      getOtherReasonParts(REQUOTE_REASONS, quote.requoteReason);

    const {
      reason: marketingTemplateExcludeReason,
      otherReason: marketingTemplateExcludeOtherReason,
    } = getOtherReasonParts(
      MARKETING_PRESENTATION_REASON_LABELS,
      quote.marketingTemplateExcludeReason,
    );

    dispatchFormState({
      type: "SET_STATE",
      payload: {
        isApprovedQuote: quote.isApproved ?? false,
        isCompetitive: quote.isCompetitive ?? false,
        isPackageRatesApply: quote.packageRatesApply ?? false,
        includeInMarketingPresentation:
          quote.includeInMarketingPresentation ?? false,
        requoteNeeded: quote.requoteRequired,
        requoteReason,
        requoteOtherReason,
        marketingTemplateExcludeReason,
        marketingTemplateExcludeOtherReason,
      },
    });
  };

  const handleOnGeneratePresentation = async (
    template: string,
    orderedCarriers: string[],
  ): Promise<void> => {
    setSubmitPresentationLoading(true);

    generatePresentation({
      marketingPresentation: template,
      templateFolderPath: folderPath,
      orderedCarriers,
    });
  };

  const handleGeneratePresentationClick = async (): Promise<void> => {
    try {
      setGeneratePresentationLoading(true);
      const result = await axiosInstance.get(
        `/brokerportal/v1/brokerTemplates?brokerId=${brokerExternalId}`,
      );
      const templateData = result.data as {
        folderPath: string;
        templates: PresentationTemplate[];
      };
      setFolderPath(templateData.folderPath);
      setPresentationTemplates(templateData.templates);
      setOpenPresentationDialog(true);
    } catch (err) {
      createToastNotification({
        severity: "error",
        message: extractErrorMessage(err),
      });
    } finally {
      setGeneratePresentationLoading(false);
    }
  };

  const handleHeaderCTAClick = (): void => {
    if (showOpenPresentationCTA) {
      if (presentationData) {
        const { marketingPresentation } = presentationData;

        const folderPath = marketingPresentation.templateFolder;
        setGeneratePresentationLoading(true);
        generatePresentation({
          marketingPresentation: marketingPresentation.name,
          templateFolderPath: folderPath,
          orderedCarriers: carriers.map((item) => item.name),
        });
      }
    } else {
      handleGeneratePresentationClick();
    }
  };

  const handleSetFormState = (
    state: keyof FormState | Partial<FormState>,
    value: string | boolean,
  ): void => {
    setFormState(state, value);
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={3} bgcolor="white">
        <Header
          showCTA={showCTA && showMarketingPresentationCTA}
          openPresentationCTA={
            showOpenPresentationCTA && showMarketingPresentationCTA
          }
          handleClick={handleHeaderCTAClick}
          isLoading={generatePresentationLoading || isMarketingLoading}
          isDisabled={!isIncludeInMarketingPresentation}
        />
        {carriers.map((c) => (
          <TrackerCard key={c.name} name={c.name}>
            <SubmissionList
              rfpId={rfpId}
              name={c.name}
              submissions={c.submissions}
              refetchRfp={refetchRfp}
              setSelectedQuote={(quote) => {
                handleOnSelectedRow(quote, c.name, quote.productType);
              }}
            />
          </TrackerCard>
        ))}
      </Box>
      {selectedQuote ? (
        <Dialog
          open={Boolean(selectedQuote)}
          onClose={handleCloseQuoteDialog}
          maxWidth="lg"
        >
          <DialogContent sx={{ padding: 3 }}>
            <QuoteDetails
              rfpId={rfpData.ecoRfpId}
              quote={selectedQuote}
              formState={formState}
              clientName={clientName}
              setFormState={handleSetFormState}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="tertiary"
              onClick={handleCloseQuoteDialog}
              disabled={quoteMutation.isPending}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleQuoteSubmit}
              disabled={
                quoteMutation.isPending ||
                shouldDisableFormSubmission(formState)
              }
            >
              {quoteMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      <GeneratePresentationDrawer
        isOpen={openPresentationDialog}
        closeModal={handleClosePresentationDialog}
        onGeneratePresentation={handleOnGeneratePresentation}
        isGeneratingPresentation={submitPresentationLoading}
        templates={presentationTemplates}
        carriers={carriers}
        onPreview={(templateUrl) => {
          window.open(`${templateUrl}?web=1`);
        }}
      />
    </>
  );
}

function shouldDisableFormSubmission(formState: FormState): boolean {
  if (formState.requoteNeeded) {
    if (!formState.requoteReason) {
      return true;
    }
    if (formState.requoteReason === "other" && !formState.requoteOtherReason) {
      return true;
    }
  }
  if (!formState.includeInMarketingPresentation) {
    if (!formState.marketingTemplateExcludeReason) {
      return true;
    }
    if (
      formState.marketingTemplateExcludeReason === "other" &&
      !formState.marketingTemplateExcludeOtherReason
    ) {
      return true;
    }
  }

  return false;
}

const NO_INFORMATION_AVAILABLE = "No information available";

type ExtendedQuote = Quote & { shareIdentifier: string };

type SubmissionListProps = {
  name: string;
  rfpId: string;
  submissions: Submission[];
  setSelectedQuote: (arg0: ExtendedQuote | null) => void;
  refetchRfp: VoidFunction;
};

type GeneratePresentationPayload = {
  rfpId: number;
  marketingPresentation: string;
  templateFolderPath: string;
  orderedCarriers: string[];
};

function SubmissionList(props: SubmissionListProps): React.JSX.Element {
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [errorProposal, setErrorProposal] = useState<Proposal | null>(null);
  const { createToastNotification } = useToastNotificationService();
  const {
    trackCarrierTrackerRequoteNeeded,
    trackCarrierIncludedInMarketingPresentation,
    trackCarrierTrackerCoverageStatus,
  } = useAnalyticsEvents();

  function handleEditQuoteDialogClose(): void {
    setSelectedQuote(null);
  }

  const axiosInstance = useApi();
  const statusMutation = useMutation<
    StatusType[],
    Error,
    {
      id: string;
      value: string;
    }
  >({
    mutationFn: ({ id, value }) =>
      axiosInstance
        .post(
          `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/aquarium/v1/submissions/${id}`,
          {
            nextStatus: value,
          },
        )
        .then((res) => res.data as StatusType[]),
    onSuccess: () => {
      props.refetchRfp();
      createToastNotification({
        message: "Status has been updated successfully",
        severity: "success",
      });
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });
  const quoteMutation = useMutation<
    Quote,
    Error,
    {
      id: number;
      status: string | null;
      exclusionReason: string | null;
      requoteRequired: boolean | null;
      requoteReason: string | null;
    }
  >({
    mutationFn: ({ id, ...body }) =>
      axiosInstance
        .patch(
          `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/aquarium/v1/quotes/${id}/flow`,
          body,
        )
        .then((res) => res.data as Quote),
    onSuccess: () => {
      props.refetchRfp();
      handleEditQuoteDialogClose();
      createToastNotification({
        message: "Quote has been updated successfully",
        severity: "success",
      });
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });

  const proposals = props.submissions.map<Proposal>((submission) => {
    const quotes = submission.quotes;
    const quoteReceivedDate = quotes?.find(
      (quote) => quote.proposal?.receivedDate,
    )?.proposal?.receivedDate;

    const submissionErrors: SubmissionNotification[] =
      submission.notifications?.filter(
        (n): n is SubmissionNotification =>
          n.notificationType === "carrierError",
      ) ?? [];

    return {
      id: submission.id,
      receivedDate: formatDate(quoteReceivedDate),
      submittedDate: formatDate(submission.submittedAt ?? submission.createdAt),
      quotes: sortProductsByQuoteProductType(quotes) as Quote[],
      errors: submissionErrors,
      current: submission.currentStatus,
      next: submission.nextStatuses,
      ecoRfpId: submission.ecoRfpId,
      ecoRfpVersion: submission.ecoRfpVersion,
    };
  });

  return (
    <>
      {proposals.map((proposal, index) => (
        <CollapsableProposal
          index={index}
          proposal={proposal}
          key={proposal.id}
          onEdit={setSelectedQuote}
          onSelectQuote={props.setSelectedQuote}
          onViewError={(proposal) => setErrorProposal(proposal)}
          defaultExpanded={index === 0}
          renderHeader={({ num: pNum }) => (
            <Box
              component="span"
              width="100%"
              display="flex"
              alignItems="center"
            >
              <Typography
                component="span"
                flex={1}
              >{`Proposal #${pNum}`}</Typography>
              <Status
                id={proposal.id}
                current={proposal.current}
                next={proposal.next}
                isPending={statusMutation.isPending}
                onChange={(status) => {
                  statusMutation.mutate({
                    id: String(proposal.id),
                    value: status.value,
                  });
                }}
              />
            </Box>
          )}
        />
      ))}

      {errorProposal ? (
        <ErrorProposalDialog
          proposal={errorProposal}
          onClose={() => {
            setErrorProposal(null);
          }}
        />
      ) : null}

      {selectedQuote ? (
        <EditQuoteDialog
          isLoading={quoteMutation.isPending}
          quote={selectedQuote}
          statusOptions={selectedQuote.nextStatuses}
          onSave={(quote) => {
            const requoteReason = quote.requoteRequired
              ? quote.requoteReason
              : undefined;
            if (quote.status === "received") {
              trackCarrierTrackerCoverageStatus({
                carrierName: props.name,
                coverage: selectedQuote.displayName,
                requoteReason,
                status: "received",
              });
            }
            if (
              quote.status === "includedMarketing" ||
              quote.status === "excludedMarketing"
            ) {
              trackCarrierIncludedInMarketingPresentation({
                coverage: selectedQuote.displayName,
                requoteReason,
                carrierName: props.name,
                status:
                  quote.status === "includedMarketing"
                    ? "included"
                    : "excluded",
              });
            }
            trackCarrierTrackerRequoteNeeded({
              statusRequoteRequired: quote.requoteRequired
                ? "needed"
                : "not_needed",
              carrierName: props.name,
              coverage: selectedQuote.displayName,
              requoteReason,
            });
            quoteMutation.mutate({ id: selectedQuote.id, ...quote });
          }}
          onClose={handleEditQuoteDialogClose}
        />
      ) : null}
    </>
  );
}

type QuoteMutation = {
  quoteId: number;
  isApproved: boolean;
  isCompetitive: boolean;
  includeInMarketingPresentation: boolean;
  packageRatesApply: boolean;
  requoteReason: string;
  marketingTemplateExcludeReason: string;
  ecoRFPId: string;
  requoteRequired: boolean;
  ecoProposalId: string;
  ecoRFPVersion: string;
  productType: ProductType;
  shareIdentifier: string;
};

type PresentationTemplate = {
  name: string;
  url: string;
};

type GeneratePresentationResponse = {
  aquarium: {
    fullPath: string;
  };
};

type MarketingPresentationTemplateResponse = {
  id: number;
  rfpId: number;
  name: string;
  templateFolder: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type PresentationDataResponse = {
  marketingPresentation: MarketingPresentationTemplateResponse;
  marketingPresentationDocsFolderUrl: string;
};

export default CarrierTracker;
